<script setup>
import { ref } from 'vue';
import api from '../ApiClient.js';
import FormGroup from './FormGroup.vue';
import Tabs from './Tabs.vue';
import Modal from './Modal.vue';
import ThemeManager from '../ThemeManager.js';

const props = defineProps({
  user: Object,
  isOpen: Boolean
});

const emit = defineEmits(['close', 'user-updated']);

const form = ref({
  name: props.user?.name || '',
  currentPassword: '',
  password: '',
  confirmPassword: '',
  darkTheme: props.user?.darkTheme || false
});

const loading = ref(false);
const error = ref('');
const success = ref('');
const activeTab = ref('general');
const originalTheme = ref(props.user?.darkTheme || false);

const tabs = [
  { label: 'General', value: 'general' },
  { label: 'Password', value: 'password' }
];

// Reset form when user changes
const resetForm = () => {
  form.value = {
    name: props.user?.name || '',
    currentPassword: '',
    password: '',
    confirmPassword: '',
    darkTheme: props.user?.darkTheme || false
  };
  originalTheme.value = props.user?.darkTheme || false;
  error.value = '';
  success.value = '';
};

const updateTheme = (e) => ThemeManager.applyTheme(e === true || e.target?.checked);

const handleClose = () => {
  // Revert theme to original state if not saved
  if (form.value.darkTheme !== originalTheme.value) updateTheme(originalTheme.value);

  resetForm();
  emit('close');
};

const validateForm = () => {
  // Validate general settings
  if (!form.value.name.trim()) {
    error.value = 'Name is required';
    return false;
  }

  // Validate password change (only if user is on password tab and trying to change password)
  if (activeTab.value === 'password' && (form.value.password || form.value.confirmPassword)) {
    if (!form.value.currentPassword) {
      error.value = 'Current password is required to change password';
      return false;
    }

    if (!form.value.password) {
      error.value = 'New password is required';
      return false;
    }

    if (form.value.password !== form.value.confirmPassword) {
      error.value = 'Passwords do not match';
      return false;
    }

    if (form.value.password.length < 6) {
      error.value = 'Password must be at least 6 characters';
      return false;
    }
  }

  return true;
};

const handleSubmit = async () => {
  if (!validateForm()) return;

  loading.value = true;
  error.value = '';
  success.value = '';

  try {
    const updateData = {
      name: form.value.name.trim(),
      darkTheme: form.value.darkTheme
    };

    // Only include password data if user is on password tab and trying to change password
    if (activeTab.value === 'password' && form.value.password) {
      updateData.currentPassword = form.value.currentPassword;
      updateData.password = form.value.password;
    }

    const response = await api.updateProfile(updateData);
    const updatedUser = response.user; // Extract user from response

    success.value = 'Settings updated successfully';
    emit('user-updated', updatedUser);

    // Clear password fields
    form.value.currentPassword = '';
    form.value.password = '';
    form.value.confirmPassword = '';

    handleClose();
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
};

// Watch for user changes and reset form
import { watch } from 'vue';
watch(() => props.user, resetForm, { immediate: true });
</script>

<template>
  <Modal :is-open="isOpen" title="Settings" @close="handleClose">
    <form @submit.prevent="handleSubmit" class="settings-form">
      <Tabs v-model="activeTab" :tabs="tabs">
        <template #default="{ active }">
          <!-- General Settings Tab -->
          <div v-if="active === 'general'">
            <FormGroup label="Name" for="name">
              <input id="name" v-model="form.name" type="text" required :disabled="loading" />
            </FormGroup>

            <FormGroup label="Dark theme">
              <input v-model="form.darkTheme" type="checkbox" @change="updateTheme" :disabled="loading" />
            </FormGroup>
          </div>

          <!-- Password Tab -->
          <div v-if="active === 'password'">
            <FormGroup label="Current Password" for="currentPassword">
              <input id="currentPassword" v-model="form.currentPassword" type="password" :disabled="loading" />
            </FormGroup>

            <FormGroup label="New Password" for="password">
              <input id="password" v-model="form.password" type="password" :disabled="loading" />
            </FormGroup>

            <FormGroup label="Confirm New Password" for="confirmPassword">
              <input id="confirmPassword" v-model="form.confirmPassword" type="password" :disabled="loading" />
            </FormGroup>
          </div>
        </template>
      </Tabs>

      <div v-if="error" class="error">{{ error }}</div>
      <div v-if="success" class="success">{{ success }}</div>

      <div class="form-actions">
        <button type="submit" class="btn-primary" :disabled="loading">
          {{ loading ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </form>
  </Modal>
</template>
